import {Box, SxProps, Theme} from '@mui/material';
import {BadgeOutlined} from '@mui/icons-material';
import {ChangeEvent, FC, useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {employeeApi} from 'src/api';
import {useBusinessId, useAppDispatch, useAppSelector} from 'src/app/hooks';
import AMPModal from 'src/components/common/AMPModal';
import {EmployeeStatsStrip} from 'src/components/employee/EmployeeStatsStrip';
import {EmployeeGap} from 'src/components/employee/EmployeeStatsStrip/EmployeeStatsStrip.types';
import {EmployeesTable} from 'src/components/EmployeesTable';
import {EmployeeTableSearchParams} from 'src/components/EmployeesTable/EmployeesTable.types';
import {EmployeeForm} from 'src/components/forms/EmployeeForm';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {ListParamsType} from 'src/types';
import {EmployeeSearchRequest} from 'src/types/employee';
import {useListQueryState} from 'src/hooks/useListQueryState';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';

const DEFAULT_PAGE_SIZE = 100;

/**
 * Everything this screen can be looking at, and the only copy of it: each field lives in the query
 * string, so opening one employee and coming back restores the view instead of resetting it.
 *
 * `gap` is one name where the request carries two flags - `withoutTag` and `notActivated` - because
 * the two are alternatives and only one can be chosen.
 */
const QUERY_DEFAULTS = {
    page: 0,
    size: DEFAULT_PAGE_SIZE,
    keyword: '',
    paymentMode: '',
    gap: '',
    sortField: '',
    sortAsc: false,
};

/**
 * The block and the list are two cards on one page. The top margin is the app bar's: the table used
 * to be first and carried its own, and now that the block is above it the clearance lives here.
 */
const pageSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    mt: 2.5,
};

export const EmployeePage: FC = () => {
    const dispatch = useAppDispatch();

    const intl = useIntl();
    const [open, setOpen] = useState<boolean>(false);
    const dialogTitle = intl.formatMessage({id: 'createEmployeeTitle'});
    const {authInfo} = useAppSelector((state) => state.common);
    const businessId = useBusinessId();

    const [query, setQuery] = useListQueryState(QUERY_DEFAULTS);

    // The search box is controlled and types a character at a time, while the URL holds the filter
    // that has actually been applied - which the box's own debounce decides a second later. Seeded
    // from the address bar, so a restored view shows the text it is narrowed by.
    const [searchValue, setSearchValue] = useState<string>(() => query.keyword);

    // Both derived from the address bar rather than kept beside it.
    const filterParams: Pick<EmployeeSearchRequest, 'data'> = useMemo(() => ({
        data: {
            keyword: query.keyword,
            paymentMode: query.paymentMode || undefined,
            withoutTag: query.gap === 'withoutTag' ? true : undefined,
            notActivated: query.gap === 'notActivated' ? true : undefined,
            sortField: (query.sortField || undefined) as EmployeeSearchRequest['data']['sortField'],
            sortAsc: query.sortAsc,
        },
    }), [query]);

    const tableParams: ListParamsType = useMemo(
        () => ({page: query.page, size: query.size}),
        [query.page, query.size]
    );

    const {
        data: employeeData,
        refetch: employeeRefetch,
        isFetching,
        isLoading,
        isError,
        error,
    } = employeeApi.useSearchEmployeesQuery({
        data: {
            // Their company when they have one; the business being worked in otherwise. A platform
            // administrator has neither of their own, so this listed every business's employees.
            businessId,
            companyId: authInfo?.company?.id,
            keyword: filterParams.data.keyword,
            sortAsc: filterParams.data.sortAsc || false,
            sortField: filterParams.data.sortField,
            paymentMode: filterParams.data.paymentMode,
            withoutTag: filterParams.data.withoutTag,
            notActivated: filterParams.data.notActivated,
        },
        params: {
            page: tableParams.page,
            size: tableParams.size,
        },
    });

    // The cells' own filters are left out: the server ignores them anyway, and keeping them out of
    // the key means clicking a cell does not refetch numbers that cannot have changed.
    const {data: employeeStats} = employeeApi.useGetEmployeeStatsQuery({
        businessId,
        companyId: authInfo?.company?.id,
        keyword: filterParams.data.keyword,
    });

    // Mode and gap are alternatives, not filters to combine: each selection clears the other, and
    // clicking the active one clears it.
    const handleSelectMode = (mode: string) => {
        setQuery({
            ...query,
            page: 0,
            gap: '',
            paymentMode: query.paymentMode === mode ? '' : mode,
        });
    };

    const handleSelectGap = (gap: EmployeeGap) => {
        setQuery({
            ...query,
            page: 0,
            paymentMode: '',
            gap: query.gap === gap ? '' : gap,
        });
    };

    const handleOpenModal = () => {
        setOpen(true);
    };

    const handleCloseModal = () => {
        setOpen(false);
        employeeRefetch();
    };

    // The toolbar controls the search text and the ordering and knows nothing of the cells' filters,
    // so only those three fields are taken from it; the rest of the address bar is left as it stands.
    const handleChange = (value: EmployeeTableSearchParams) => {
        setQuery({
            ...query,
            page: 0,
            keyword: value.keyword,
            sortField: value.sortField ?? '',
            sortAsc: value.sortAsc,
        });
    };

    const handleChangePage = (page: number) => setQuery({...query, page});

    const handleChangeRowsNumber = (e: ChangeEvent<HTMLInputElement>) => setQuery({
        ...query,
        page: 0,
        size: +e.target.value || DEFAULT_PAGE_SIZE,
    });

    useEffect(() => {
        const isDataLoading = isFetching || isLoading;

        if (isDataLoading) {
            dispatch(preloaderChangeStatus(true));
        } else {
            dispatch(preloaderChangeStatus(false));
        }
    }, [isFetching, isLoading]);

    useEffect(() => {
        if (isError && isErrorObject(error)) {
            NotifyError(error.data.message);
        }
    }, [isError, error]);

    return (
        <Box sx={pageSx}>
            <EmployeeStatsStrip
                data={employeeStats}
                activeMode={filterParams.data.paymentMode}
                onSelectMode={handleSelectMode}
                activeGap={
                    filterParams.data.withoutTag
                        ? 'withoutTag'
                        : filterParams.data.notActivated
                            ? 'notActivated'
                            : undefined
                }
                onSelectGap={handleSelectGap}
            />
            {employeeData && !isFetching && !isLoading && (
                <EmployeesTable
                    size={tableParams.size}
                    data={employeeData}
                    pageNumber={tableParams.page}
                    handleUpdateTableData={handleChange}
                    handleChangePage={handleChangePage}
                    handleChangeRowsNumber={handleChangeRowsNumber}
                    searchValue={searchValue}
                    setSearchValue={setSearchValue}
                    title={intl.formatMessage({id: 'employees'})}
                    onAddClick={handleOpenModal}
                    buttonText="addEmployee"
                    icon={<BadgeOutlined/>}
                />
            )}

            <AMPModal
                handleClose={handleCloseModal}
                open={open}
                title={dialogTitle}
                type="md"
                fullWidth
                withoutPaddings
            >
                <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
                    <EmployeeForm handleClose={handleCloseModal}/>
                </Box>
            </AMPModal>
        </Box>
    );
};
